import { useState } from 'react';
import { Banner, Button, Modal, TextArea } from '@servicecompressionllc/sc-design-system';
import { Info } from '@servicecompressionllc/sc-design-system/icons';
import { PeoplePicker } from './PeoplePicker.js';
import type { SharePerson } from './shareClient.js';
import type { ConversationShareControls } from './useSharingLogic.js';

interface ShareDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  controls: ConversationShareControls;
}

const shareLabel = (count: number): string =>
  count === 0 ? 'Share' : `Share with ${String(count)} ${count === 1 ? 'person' : 'people'}`;

/** Pick people and an optional note, then share the conversation as it is now. */
export const ShareDialog = ({ open, onOpenChange, controls }: ShareDialogProps) => {
  const [people, setPeople] = useState<SharePerson[]>([]);
  const [note, setNote] = useState('');
  const { shareWith } = controls;

  const close = () => {
    setPeople([]);
    setNote('');
    shareWith.reset();
    onOpenChange(false);
  };
  const submit = () => {
    const trimmed = note.trim();
    shareWith.mutate(
      { userIds: people.map((p) => p.userId), ...(trimmed ? { note: trimmed } : {}) },
      { onSuccess: close },
    );
  };

  return (
    <Modal open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      <Modal.Header>
        <Modal.Title>Share this conversation</Modal.Title>
        <Modal.CloseButton />
      </Modal.Header>
      <Modal.Body className="flex flex-col gap-4">
        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="share-people"
            className="text-xs font-semibold uppercase tracking-wider text-ink-subtle"
          >
            People
          </label>
          <PeoplePicker
            id="share-people"
            selected={people}
            onChange={setPeople}
            placeholder="Name or email"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="share-note"
            className="text-xs font-semibold uppercase tracking-wider text-ink-subtle"
          >
            Note (optional)
          </label>
          <TextArea
            id="share-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={1000}
            className="min-h-20"
          />
        </div>
        <Banner variant="info">
          <Banner.Icon>
            <Info size={16} aria-hidden />
          </Banner.Icon>
          <Banner.Body>
            <Banner.Description>
              They’ll see this entire conversation, every question and answer so far, word for word.
              Anything you ask after sharing stays private.
            </Banner.Description>
          </Banner.Body>
        </Banner>
        {shareWith.error && (
          <p className="text-xs font-semibold text-danger">{shareWith.error.message}</p>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="ghost" onClick={close}>
          Cancel
        </Button>
        <Button onClick={submit} disabled={people.length === 0 || shareWith.isPending}>
          {shareLabel(people.length)}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
